import React, { useRef, useState } from 'react';
import { Cropper } from 'react-cropper';
import { Button, Group, Image, Box } from '@mantine/core';
import 'cropperjs/dist/cropper.css';

interface ImageCropperProps {
  src: string; // The base64 or URL of the original image
  aspectRatio?: number;
  setCroppedBlob: (blob: Blob) => void;
}

export default function ImageCropper({ src, aspectRatio, setCroppedBlob }: ImageCropperProps) {
  const cropperRef = useRef<HTMLImageElement>(null);
  const [croppedPreviewUrl, setCroppedPreviewUrl] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(true);

  // Called when user clicks "Crop" button
  const handleCrop = () => {
    const cropper = (cropperRef.current as any)?.cropper;
    if (!cropper) return;

    const canvas = cropper.getCroppedCanvas();
    if (!canvas) return;

    // 1. Create a data URL for a preview <img>
    const dataUrl = canvas.toDataURL('image/png');
    setCroppedPreviewUrl(dataUrl);

    // 2. Create a Blob for uploading
    canvas.toBlob((blob: Blob | null) => {
      if (blob) {
        setCroppedBlob(blob);
      }
    }, 'image/png');

    // 3. Hide the cropper, show the preview instead
    setShowCropper(false);
  };

  // Optional reset: clear the crop box and preview
  const handleReset = () => {
    const cropper = (cropperRef.current as any)?.cropper;
    cropper?.reset();
    setCroppedPreviewUrl(null);
  };

  return (
    <Box>
      {showCropper && (
        <Box>
          <Cropper
            ref={cropperRef}
            src={src}
            style={{ height: 400, width: '100%' }}
            guides
            aspectRatio={aspectRatio || 16 / 9}
            viewMode={1}
          />

          <Group mt="xs">
            <Button
              onClick={handleCrop}
              sx={{
                backgroundColor: '#EA580C',
                '&:hover': { backgroundColor: '#c44b0a' },
              }}
            >
              Crop
            </Button>
          </Group>
        </Box>
      )}

      {croppedPreviewUrl && (
        <Box mt="md">
          <Image src={croppedPreviewUrl} alt="Cropped Preview" radius="md" />
        </Box>
      )}
    </Box>
  );
}
